import { useId, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionIcon, Alert, Anchor, Collapse, Group, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconArrowLeft, IconChevronDown, IconRefresh } from '@tabler/icons-react';
import DealPrice from '@/components/app/deals/DealPrice';
import DealStatusBadge from '@/components/app/deals/DealStatusBadge';
import PageTitle from '@/components/common/PageTitle';
import { buildDealPriceModel, goodsSpecFactDiff } from '@/lib/dealPrice';
import { formatMoney, formatOrgName, formatProperty, headlineQuality } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { BargainStepsInfo } from '@/types/deals';
import classes from './DealHeader.module.css';

interface DealHeaderProps {
  readonly info: BargainStepsInfo;
  readonly onRefresh: () => void;
  readonly refreshing: boolean;
  /** Компактное действие в правом верхнем углу (кнопка чата) — перед «Обновить». */
  readonly chatAction?: ReactNode;
}

// Шапка сделки (образец b-deal): слева товар и номер сделки, справа сумма по роли (FR-BRG-50), ниже полоса фактов.
// Продавцу — сумма за товар по спецификации и, после окончательного расчёта, факт с разницей. Покупателю и агенту —
// полная сумма с логистикой и раскрытие «Из чего складывается» (разбивка DealPrice).
export default function DealHeader({ info, onRefresh, refreshing, chatAction }: DealHeaderProps) {
  const { t } = useTranslation();
  const [breakdownOpened, { toggle: toggleBreakdown }] = useDisclosure(false);
  const breakdownId = useId();
  const dealNumber = info.specNum ?? info.contractNum;
  const quality = headlineQuality(info.goodsProperties);
  const model = buildDealPriceModel(info);
  const outcome = goodsSpecFactDiff(info); // товар: по спецификации → факт → разница (FCA, без НДС)
  const rub = t('app.deals.rub');
  // Сумма по спецификации — как в списке и на «Главной». Покупателю и агенту — полная по цене CPT от сервера:
  // она закреплена спецификацией, а сумма по ставкам доставки может с ней разойтись, если ставка перевозки
  // изменилась позже.
  const specTotal = model.sellerView ? outcome.spec : (info.payment?.fullPriceCPTWithoutNds ?? model.grandTotal);
  // Факт строкой — только продавцу (товар по факту). Покупатель и агент видят факт в разбивке и в окончательном
  // расчёте: своя «разница» в шапке разошлась бы с суммой к доплате, которая считается по товару.
  const factTotal = model.sellerView ? outcome.fact : undefined;
  const factDiff = factTotal != null && specTotal != null ? factTotal - specTotal : undefined;
  // Год урожая сделки необязателен (решение 2026-11-04): «урожай 2025» — только если задан, иначе ничего.
  const lead = [
    dealNumber ? t('app.deals.header.dealNo', { no: dealNumber }) : null,
    quality ? formatProperty(quality) : null,
    info.cropYear ? t('app.sell.cropYear', { year: info.cropYear }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Пустые ячейки не выводим: «—» в полосе фактов ничего не сообщает.
  const facts = [
    { key: 'seller', label: t('app.deals.summary.seller'), value: formatOrgName(info.seller?.name) },
    { key: 'buyer', label: t('app.deals.summary.buyer'), value: formatOrgName(info.buyer?.name) },
    {
      key: 'weight',
      label: t('app.deals.header.weight'),
      value: info.plannedWeight != null ? `${formatMoney(info.plannedWeight)} ${t('app.deals.tons')}` : undefined,
    },
    {
      key: 'price',
      label: t('app.deals.header.price'),
      value: info.price != null ? `${formatMoney(info.price)} ${t('app.deals.rubPerTon')}` : undefined,
    },
    {
      key: 'distance',
      label: t('app.deals.header.distance'),
      value: info.distance != null ? `${info.distance} ${t('app.deals.km')}` : undefined,
    },
  ].filter((fact): fact is { key: string; label: string; value: string } => Boolean(fact.value));

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Anchor component={Link} to={ROUTES.APP_DEALS} c="gray.7" fz="sm" underline="never">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={16} />
            {t('app.deals.back')}
          </Group>
        </Anchor>
        <Group gap="xs" wrap="nowrap">
          {chatAction}
          <Tooltip label={t('app.deals.refresh')}>
            <ActionIcon variant="default" onClick={onRefresh} loading={refreshing} aria-label={t('app.deals.refresh')}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <div>
        <div className={classes.head}>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
              {info.goodsType ?? lead}
            </PageTitle>
            <Group gap="sm" wrap="wrap" align="center">
              {lead && info.goodsType ? <Text c="gray.7">{lead}</Text> : null}
              {/* У сделки в работе о состоянии говорит шаг; статус показываем завершённой, отменённой и обрабатываемой. */}
              {info.bargainStatus && info.bargainStatus !== 'IN_PROGRESS' ? <DealStatusBadge status={info.bargainStatus} /> : null}
            </Group>
          </Stack>

          {specTotal != null ? (
            <div className={classes.total}>
              <Text size="sm" c="gray.7">
                {t(model.sellerView ? 'app.deals.header.bySpecNoNds' : 'app.deals.header.withLogistics')}
              </Text>
              <div className={classes.totalValue}>
                {formatMoney(specTotal)} {rub}
              </div>
              {factTotal != null ? (
                <Text size="sm" c="gray.7">
                  {t('app.deals.header.fact')} {formatMoney(factTotal)} {rub}
                  {factDiff != null && Math.abs(factDiff) > 1
                    ? ` · ${t('app.deals.header.difference')} ${factDiff > 0 ? '+' : '−'}${formatMoney(Math.abs(factDiff))} ${rub}`
                    : ''}
                </Text>
              ) : null}
              {model.sellerView ? null : (
                <UnstyledButton
                  onClick={toggleBreakdown}
                  aria-expanded={breakdownOpened}
                  aria-controls={breakdownId}
                  fz="sm"
                  fw={500}
                  c="brand.7"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>{t('app.deals.header.breakdown')}</span>
                  <IconChevronDown
                    size={14}
                    aria-hidden
                    style={{ transform: breakdownOpened ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }}
                  />
                </UnstyledButton>
              )}
            </div>
          ) : null}
        </div>

        {model.sellerView ? null : (
          <Collapse expanded={breakdownOpened}>
            <div id={breakdownId} className={classes.breakdown}>
              <DealPrice info={info} />
            </div>
          </Collapse>
        )}

        {facts.length > 0 ? (
          <dl className={classes.facts} style={{ '--facts-count': facts.length } as CSSProperties}>
            {facts.map((fact) => (
              <div key={fact.key} className={classes.fact}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {info.loadingAddress != null || info.unloadingAddress != null ? (
          <Stack gap={2} mt="xs">
            <Text fz="sm" c="gray.7">
              {t('app.deals.header.loading')}: {info.loadingAddress ?? '—'}
            </Text>
            <Text fz="sm" c="gray.7">
              {t('app.deals.header.unloading')}: {info.unloadingAddress ?? '—'}
            </Text>
          </Stack>
        ) : null}
      </div>

      {info.bargainStatus === 'WAIT_BPMN' ? (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={18} />} py="xs">
          {t('app.deals.processing')}
        </Alert>
      ) : null}
    </Stack>
  );
}
